import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { URL } from '../../../common/api/urls';
import useRefreshToken from '../../../common/hooks/useRefreshToken';

const style = {
  position: 'absolute',
  top: '48%',
  left: '48%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '550px' },
  maxWidth: '550px',
  bgcolor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  p: '30px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  animation: 'slideIn 0.5s ease',
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translate(-50%, -50%) translateY(-30px) scale(0.9)'
    },
    to: {
      opacity: 1,
      transform: 'translate(-50%, -50%) translateY(0) scale(1)'
    }
  }
};

// Confirmation Modal Component
function ConfirmationModal({ open, onClose, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.4s ease',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 }
          }
        }
      }}
    >
      <Box sx={style}>
        <Typography 
          id="confirmation-modal-title" 
          component="h2"
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#333333',
            marginBottom: '15px',
            textAlign: 'center',
            lineHeight: 1.2
          }}
        >
          Confirm Demo Start
        </Typography>

        <Typography 
          id="confirmation-modal-description" 
          sx={{ 
            color: '#555555',
            lineHeight: 1.6,
            marginBottom: '20px',
            fontSize: '18px',
            textAlign: 'center'
          }}
        >
          {/* Due to an overwhelming demand, our free 2-day Demo slots are all full right now and we have suspended the auto-subscribe option. However if you wish to try the charts before purchasing an Elite plan, please send an email to <u>charts@vtrender.com</u> and we will review and consider your request. */}
          You have chosen to start a 2-day Demo of an <Box component="span" color="#EC6C10" sx={{fontWeight: 700}}>Elite Index</Box> plan. Would you like to proceed?
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          gap: '15px', 
          flexDirection: { xs: 'column', sm: 'row' },
          marginTop: '20px'
        }}>
          <Button
            variant="contained"
            onClick={onConfirm}
            sx={{ 
              flex: 1,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#ffffff',
              padding: '12px 25px',
              border: 'none',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                background: 'linear-gradient(135deg, #5a6fd8, #6a4190)'
              }
            }}
          >
            Yes! Start Demo
            {/* Ok */}
          </Button>
          
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ 
              flex: 1,
              background: 'transparent',
              color: '#667eea',
              border: '2px solid #667eea',
              padding: '12px 25px',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                background: '#667eea',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

// Success Popup Component
function SuccessPopup({ open, onClose }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="success-modal-title"
      aria-describedby="success-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.4s ease',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 }
          }
        }
      }}
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '420px' },
        maxWidth: '420px',
        bgcolor: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
        p: '40px 30px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        animation: 'slideIn 0.5s ease',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translate(-50%, -50%) translateY(-30px) scale(0.9)'
          },
          to: {
            opacity: 1,
            transform: 'translate(-50%, -50%) translateY(0) scale(1)'
          }
        }
      }}>

        <Typography 
          id="success-modal-title" 
          component="h2"
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#333333',
            marginBottom: '12px',
            textAlign: 'center',
            lineHeight: 1.2,
            animation: 'fadeInUp 0.6s ease 0.2s both',
            '@keyframes fadeInUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)'
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }
          }}
        >
          Demo Started!
        </Typography>

        <Typography 
          id="success-modal-description" 
          sx={{ 
            color: '#666666',
            lineHeight: 1.6,
            marginBottom: '30px',
            fontSize: '16px',
            textAlign: 'center',
            animation: 'fadeInUp 0.6s ease 0.3s both',
            '@keyframes fadeInUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)'
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }
          }}
        >
          Your Elite Index Demo plan is now active. Enjoy exploring premium features!
        </Typography>

        {/* Feature highlights */}
        <Box sx={{
          background: 'linear-gradient(135deg, #f8f9ff, #e8f0ff)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '30px',
          border: '1px solid rgba(102, 126, 234, 0.1)',
          animation: 'fadeInUp 0.6s ease 0.4s both',
          '@keyframes fadeInUp': {
            from: {
              opacity: 0,
              transform: 'translateY(20px)'
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}>
          <Typography sx={{
            fontSize: '13px',
            color: '#667eea',
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: '8px'
          }}>
            ✨ What's included:
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: '#555555',
            textAlign: 'center',
            lineHeight: 1.5
          }}>
            Order Flow, Market Depth, Volume Profile & Smart Money Tools
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          animation: 'fadeInUp 0.6s ease 0.5s both',
          '@keyframes fadeInUp': {
            from: {
              opacity: 0,
              transform: 'translateY(20px)'
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#ffffff',
              padding: '14px 36px',
              border: 'none',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontSize: '15px',
              minWidth: '120px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                background: 'linear-gradient(135deg, #5a6fd8, #6a4190)'
              },
              '&:active': {
                transform: 'translateY(0)',
                transition: 'all 0.1s ease'
              }
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

// Main Component
export default function DemoConfirmationPopup(props) {
  const [cancelPlanSubReq,responsePlanSubReq,errorPlanSubReq,loadedPlanSubReq,resetPlanSubReq,getPlanSubStatus] = useAxiosWithAuth();
  
  console.log("DemoConfirmationPopup props=", props);
  const [open, setOpen] = React.useState(props.open);
  const [showSuccessPopup, setShowSuccessPopup] = React.useState(false);
  const planName = "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_2";
  const refresh = useRefreshToken();

  const startDemo=()=>{
    console.log("starting demo")
    let data={"plan_name": planName};
    getPlanSubStatus(`${URL.DEMO_SUB_CHECK}`,"POST",data);
  }

  const verifyRefreshToken = async () => {
    try {
        await refresh(true);
    }
    catch (err) {
        console.error(err);
    }
    finally {
      // do a reload here
      let reload_url=`${process.env.REACT_APP_URL}subscription`
      window.location=reload_url
    }
  }


  


  

  React.useEffect(() => {  
      if(loadedPlanSubReq){
        console.log("demo loaded data ",responsePlanSubReq);
        if(responsePlanSubReq?.success ){
          console.log("demo activate success");
          verifyRefreshToken();
          console.log("demo token refreshed")
          setShowSuccessPopup(true);
        }
        else{
          console.log("Failed to subsribe to Demo plan! Please check with support for more details on the issue.");
          alert("Failed to subsribe to Demo plan! Please check with support for more details on the issue.");
        }
        resetPlanSubReq();
      }
      else if(errorPlanSubReq!==null){
        console.log("Error demo data=",errorPlanSubReq);
      }
      console.log("demo activate final")
      resetPlanSubReq();
    },[loadedPlanSubReq,responsePlanSubReq, errorPlanSubReq]);

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  const handleConfirm = () => {
    startDemo();
    setOpen(false)
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
  };

  return (
    <>
      <ConfirmationModal 
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <SuccessPopup 
        open={showSuccessPopup}
        onClose={handleSuccessClose}
      />
    </>
  );
}